import { Component, OnDestroy, HostListener } from '@angular/core';
import { Logger } from '@app/@shared';
import { AuthService } from '@app/@shared/services/auth.service';
import { NavController, PopoverController, ModalController } from '@ionic/angular';
import { LoginModalComponent } from '@app/auth/login-modal.component';
import { ProfileComponent } from './popover/profile/profile.component';
import { Live } from '@app/@shared/models/live.model';
import { GameHomeService } from '@app/@shared/services/game-home.service';
import { GameHome } from '@app/@shared/models/game-home.model';
import { DateService } from '@app/@shared/services/date.service';
import { Subscription, firstValueFrom, interval } from 'rxjs';
import { GeneralService } from '@app/@shared/services/general.service';
import { Timestamp } from 'firebase/firestore';
import { environment } from '@env/environment';
import { WalletService } from '@app/@shared/services/wallet.service';
import { Draw, ResponseSD } from '@app/@shared/models/lottery.model';
import { TrackingService } from '@app/@shared/services/tracking.service';
import { Credentials, CredentialsService } from '@app/auth';
import { UserService } from '@app/@shared/services/user.service';
import { IpApiService } from '@app/@shared/services/ip-api/ip-api.service';
import { DrawsAvailablesService } from '@app/@shared/services/draws-availables.service';
import { SorteoService } from '@app/@shared/services/sorteo.service';
import { LotenalService } from '@app/@shared/services/lotenal.service';
import { BannerService } from '@app/@shared/services/banner.service';
import { Banner } from '@app/@shared/models/banner.model';
import { CardsPromo } from '@shared/models/cards-promo.model';
import { CardsPromoService } from '@shared/services/cards-promo.service';
import { Platform } from '@ionic/angular';
import { BrandingService } from '@shared/services/branding.service';
import { LiveService } from '@app/@shared/services/live.service';
import { GameLogoService } from '@app/@shared/services/game-logo.service';
import { resolveGameLogoSrc, isRemoteLogoUrl } from '@app/@shared/utils/game-logo.util';

const log = new Logger('home-component');

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnDestroy {
  isLoading = true;
  games: GameHome[] = [];
  selectedFilter: 'electronico' | 'loteria' = 'electronico';
  filteredGames: GameHome[] = [];
  trackedGame: string = '';
  sorteosHeader: any[] = [];
  lives: Live[] = [];
  now?: Date;
  loteriaNow?: Date;
  promos: CardsPromo[] = [];
  invalidDate = false;
  noLoteriaDrawsAvailable = false;
  loteriaErrorMessage = '';
  banners: Banner[] = [];
  bannersLoaded = false;
  private subscriptions: Subscription[] = [];
  private lotenalDrawsSub?: Subscription;
  balance = 0;
  countdownTimers: { [key: string]: string } = {};
  gamesSlide = [
    {
      class: 'progol',
      route: '/game/progol',
      icon: 'progol-revancha',
      message: '¡Anota y Gana!',
    },
    {
      class: 'melate',
      route: '/game/melate',
      icon: 'melate-revancha-revanchita',
      message: 'Juega y Gana!',
    },
    {
      class: 'melateretro',
      route: '/game/melate/retro',
      icon: 'melate-retro',
      message: '¡Melate Retro! Regresa a la suerte clásica',
    },
    {
      class: 'tris',
      route: '/game/tris',
      icon: 'tris',
      message: 'Juega Tris con Multiplicador',
    },
    {
      class: 'progolMedia',
      route: '/game/progol/media-semana',
      icon: 'progol-media-semana',
      message: '¡Juega tus partidos favoritos desde $15!',
    },
    {
      class: 'ganagato',
      route: '/game/gana-gato',
      icon: 'ganagato',
      message: 'Juega desde $10',
    },
    {
      class: 'protouch',
      route: '/game/protouch',
      icon: 'protouch',
      message: 'Juega desde $10',
    },
    {
      class: 'pronosports',
      route: '/game/prono-sports',
      icon: 'prono-sports',
      message: '¡Juega tus partidos favoritos desde $10!',
    },
  ];
  additionalSlides: any[] = [];
  get credentials(): Credentials | null {
    return this.credentialsService.credentials;
  }

  isCierreSorteosActive = false;

  isSmallScreen = false;

  private modalOpen = false;

  constructor(
    private authS: AuthService,
    private dateS: DateService,
    public walletS: WalletService,
    private navCtrl: NavController,
    private dateService: DateService,
    private generalS: GeneralService,
    private popoverCtrl: PopoverController,
    private gameHomeService: GameHomeService,
    private trackingS: TrackingService,
    private userS: UserService,
    private credentialsService: CredentialsService,
    private servIP: IpApiService,
    public drawsAS: DrawsAvailablesService,
    private sorteoS: SorteoService,
    private lotenalS: LotenalService,
    private bannerService: BannerService,
    private cardsPromoService: CardsPromoService,
    private modalCtrl: ModalController,
    public brandingService: BrandingService,
    private liveS: LiveService,
    private gameLogoS: GameLogoService
  ) {}

  /** Logo desde BD (gamesHome.logo) o fallback a frames locales. */
  getGameLogoSrc(item: GameHome): string {
    return item.dynamicLogoUrl || resolveGameLogoSrc(item.logo);
  }

  /**
   * Lotería con URL remota → contain.
   * Electrónico con logo de BD → conserva fondo de color de marca (los frames locales ya lo traían en el SVG).
   */
  getLogoContainerClasses(item: GameHome): Record<string, boolean> {
    const fromBd = !!(item.dynamicLogoUrl && isRemoteLogoUrl(item.dynamicLogoUrl)) || isRemoteLogoUrl(item.logo);

    if (item.type === 'loteria') {
      return { 'is-loteria': fromBd || !!item.dynamicLogoUrl };
    }

    if (item.type === 'electronico' && fromBd) {
      const colorClass = this.getElectronicLogoColorClass(item);
      return {
        'logo-from-bd': true,
        [colorClass]: true,
      };
    }

    return {};
  }

  hasRemoteElectronicLogo(item: GameHome): boolean {
    if (item.type !== 'electronico') return false;
    return !!(item.dynamicLogoUrl && isRemoteLogoUrl(item.dynamicLogoUrl)) || isRemoteLogoUrl(item.logo);
  }

  private getElectronicLogoColorClass(item: GameHome): string {
    const key = String(item.product || item.name || item.logo || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s_-]+/g, '');

    if (key.includes('melateretro') || (key.includes('retro') && key.includes('melate'))) return 'logo-melateretro';
    if (key.includes('melate')) return 'logo-melate';
    if (key.includes('chispazo')) return 'logo-chispazo';
    if (key.includes('tris') || key.includes('tri$')) return 'logo-tris';
    if (key.includes('ganagato')) return 'logo-ganagato';
    if (key.includes('progolmedia') || key.includes('mediasemana')) return 'logo-progolmedia';
    if (key.includes('protouch')) return 'logo-protouch';
    if (key.includes('pronosports') || key.includes('miprogol')) return 'logo-pronosports';
    if (key.includes('progol')) return 'logo-progol';

    return 'logo-default';
  }

  /** Logo de electrónico por producto/nombre para carrusel y UI hardcodeada. */
  electronicLogo(key: string, fallback: string): string {
    return this.gameLogoS.getLogo(key, fallback);
  }

  get isAuthenticated(): boolean {
    return this.credentialsService.isAuthenticated();
  }

  async handleCardClick(route: string, event?: Event): Promise<void> {
    // Si hay evento, prevenir comportamiento por defecto
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Si no está autenticado, mostrar modal de login
    if (!this.isAuthenticated) {
      if (!this.modalOpen) {
        this.modalOpen = true;

        const modal = await this.modalCtrl.create({
          component: LoginModalComponent,
          backdropDismiss: false,
          cssClass: 'login-modal',
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        this.modalOpen = false;

        // Si el login fue exitoso, navegar a la ruta
        if (data?.success && this.isAuthenticated) {
          this.navCtrl.navigateForward([route]);
        }
      }
    } else {
      // Si está autenticado, navegar normalmente
      this.navCtrl.navigateForward([route]);
    }
  }

  async openBannerUrl(url?: string, event?: Event): Promise<void> {
    if (!url) return;

    // Prevenir comportamiento por defecto y propagación
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Si es una URL externa (http:// o https://), abrir en la misma ventana
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.location.href = url;
      return;
    }

    // Si es una ruta interna, asegurarse de que empiece con /
    const route = url.startsWith('/') ? url : `/${url}`;

    // Permitir siempre acceso a rutas legales y home sin autenticación
    if (route.includes('/legal') || route === '/home' || route === '/') {
      this.navCtrl.navigateForward([route], { queryParamsHandling: 'preserve' });
      return;
    }

    // Si no está autenticado, mostrar modal de login
    if (!this.isAuthenticated) {
      if (!this.modalOpen) {
        this.modalOpen = true;

        const modal = await this.modalCtrl.create({
          component: LoginModalComponent,
          backdropDismiss: false,
          cssClass: 'login-modal',
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
        this.modalOpen = false;

        // Si el login fue exitoso, navegar a la ruta
        if (data?.success && this.isAuthenticated) {
          this.navCtrl.navigateForward([route]);
        }
        // Si el usuario cerró el modal sin hacer login, no hacer nada (permanecer en home)
      }
    } else {
      // Si está autenticado, navegar normalmente
      this.navCtrl.navigateForward([route]);
    }
  }

  async ionViewDidEnter() {
    this.brandingService.detectBrand();
    this.checkScreenSize();
    this.isCierreSorteosActive = await this.dateS.verifyCloseDayIGT();
    if (!this.games.length) this.loadData();

    // Check periodically (every 30 seconds)
    const timeSub = interval(30000).subscribe(async () => {
      this.isCierreSorteosActive = await this.dateS.verifyCloseDayIGT();
    });
    this.subscriptions.push(timeSub);
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenSize();
  }

  private checkScreenSize() {
    // Define tu umbral, por ejemplo 768px (tableta o menor → versión móvil)
    const wasSmallScreen = this.isSmallScreen;
    this.isSmallScreen = window.innerWidth <= 768;

    // Si cambió el tamaño de pantalla, recargar las imágenes
    if (wasSmallScreen !== this.isSmallScreen && this.banners.length > 0) {
      this.preloadBannerImages();
    }
  }
  ionViewDidLeave() {
    this.lotenalDrawsSub?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  ngOnDestroy() {
    this.lotenalDrawsSub?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }

  async handleRefresh(event: CustomEvent) {
    this.checkScreenSize();
    await this.loadData(true);

    // Any calls to load data go here
    (event.target as HTMLIonRefresherElement).complete();
  }

  openUrl(url: string | undefined) {
    if (url) {
      window.open(url);
    }
  }

  async loadData(isRefresh = false) {
    if (!isRefresh) this.isLoading = true;
    this.bannersLoaded = false;
    this.now = await this.dateS.dateNow();
    this.isCierreSorteosActive = await this.dateS.verifyCloseDayIGT();
    this.loteriaNow = await this.dateS.getPreciseNow();
    await this.loadGames();
    await this.gameLogoS.ensureLoaded();
    if (this.brandingService.isMidiadesuerte()) {
      this.loadGamesHeader();
      this.loadLives();
    }
    if (!isRefresh) this.isLoading = false;

    // this.bannerService.getBanners().subscribe(async (data) => {
    //   this.banners = data;
    //   // Pre-cargar imágenes antes de mostrarlas
    //   await this.preloadBannerImages();
    //   this.bannersLoaded = true;
    // });
    if (!this.brandingService.isMidiadesuerte()) {
      this.bannerService.getBanners().subscribe(async (data) => {
        // 1. Asignamos la data inicial
        this.banners = data;

        // 2. Procesamos cada banner para ver si lo tenemos en local
        const processedBanners = await Promise.all(
          this.banners.map(async (banner) => {
            const targetUrl = this.isSmallScreen ? banner.imageUrlPhone : banner.imageUrl;
            const cacheKey = this.isSmallScreen ? `mob_${banner.id}` : `web_${banner.id}`;

            if (targetUrl) {
              const localData = await this.getAndCacheImage(targetUrl, cacheKey);
              // Reemplazamos la URL por la data Base64
              if (this.isSmallScreen) {
                banner.imageUrlPhone = localData;
              } else {
                banner.imageUrl = localData;
              }
            }
            return banner;
          })
        );

        this.banners = processedBanners;
        this.bannersLoaded = true;
      });

      this.cardsPromoService.getPromos().subscribe((data) => {
        this.promos = data;
      });
    } else {
      this.bannersLoaded = true;
    }
  }

  private async preloadBannerImages(): Promise<void> {
    const imagePromises: Promise<void>[] = [];

    this.banners.forEach((banner) => {
      const imageUrl = this.isSmallScreen ? banner.imageUrlPhone : banner.imageUrl;
      if (imageUrl) {
        const promise = new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve(); // Resolver incluso si falla para no bloquear
          img.src = imageUrl;
        });
        imagePromises.push(promise);
      }
    });

    // Esperar a que todas las imágenes se carguen o un timeout máximo
    await Promise.race([
      Promise.all(imagePromises),
      new Promise<void>((resolve) => setTimeout(() => resolve(), 3000)), // Timeout de 3 segundos
    ]);
  }

  subscribeGamesHome() {
    const subscribeHome = this.gameHomeService.getAllGames().subscribe(async (data) => {
      const now = await this.dateS.dateNow();
      // console.log('NOW: ', now);

      if (isNaN(now.getTime())) {
        console.error('Fecha inválida obtenida: ', now);
        this.invalidDate = true;
        return;
      }

      const chispazoDate = (await this.generalS.generateMexicoCityDatesFromHours([15, 21], 6))[0];
      const ganaGato = this.generalS.adjustDatesToMexicoCityTime(
        await this.generalS.getDatesByDayWeek(10, [2, 4, 6]),
        21,
        now
      )[0];
      const melate = this.generalS.adjustDatesToMexicoCityTime(
        await this.generalS.getDatesByDayWeek(10, [0, 3, 5]),
        21,
        now
      )[0];
      const tris = (await this.generalS.generateMexicoCityDatesFromHours([13, 15, 17, 19, 21], 3))[0];

      this.games = data
        .filter((d) => d.isActive)
        .map((g) => {
          let close: Timestamp | undefined = g.close;
          let next: Timestamp | undefined = g.next;

          if (g.name.includes('Chispazo')) {
            next = Timestamp.fromDate(new Date(chispazoDate));
            chispazoDate.setSeconds(chispazoDate.getSeconds() - environment.draw.endSales);
            close = Timestamp.fromDate(new Date(chispazoDate));
          } else if (g.name.includes('Gato')) {
            next = Timestamp.fromDate(new Date(ganaGato));
            ganaGato.setSeconds(ganaGato.getSeconds() - environment.draw.endSales);
            close = Timestamp.fromDate(new Date(ganaGato));
          } else if (g.name.includes('Melate')) {
            next = Timestamp.fromDate(new Date(melate));
            melate.setSeconds(melate.getSeconds() - environment.draw.endSales);
            close = Timestamp.fromDate(new Date(melate));
          } else if (g.name.includes('Tri')) {
            next = Timestamp.fromDate(new Date(tris));
            tris.setSeconds(tris.getSeconds() - environment.draw.endSales);
            close = Timestamp.fromDate(new Date(tris));
          }

          return {
            ...g,
            close: this.ensureTimestampOrUndefined(close),
            next: this.ensureTimestampOrUndefined(next),
          };
        })
        .sort((a, b) => {
          const getDate = (item: any) => {
            if (!item?.next) return Infinity;
            // Si es Timestamp de Firebase
            if (item.next?.toDate) return item.next.toDate().getTime();
            // Si ya es una fecha
            if (item.next instanceof Date) return item.next.getTime();
            // Si es string ISO
            if (typeof item.next === 'string') return new Date(item.next).getTime();
            return Infinity;
          };

          return getDate(a) - getDate(b);
        });

      this.updateCountdowns();
      this.selectFilter(this.selectedFilter);
      this.isLoading = false;
    });

    this.subscriptions.push(subscribeHome);
  }

  private async loadGames() {
    this.subscribeGamesHome();
    // this.games = data.filter((d) => d.isActive);
  }

  // async loadGamesHeader() {
  //   const data = await firstValueFrom(this.gameHomeService.getAllGames());
  //   this.sorteosHeader = data.filter((item) => item);
  // }
  // async loadGamesLotenal() {
  //   this.isLoading = true;
  //   this.noLoteriaDrawsAvailable = false; // Resetear estado al cargar

  //   try {
  //     const response: any = await this.drawsAS.getAvailableDrawsHome();

  //     console.log('Response from getAvailableDrawsHome:', response);
  //     // Verificar si no hay sorteos disponibles
  //     const hasNoAvailableDraws =
  //       response?.lnResponseSD?.responseHeadOV?.respCodeDescription?.includes('Sorteo no disponibles');

  //     if (hasNoAvailableDraws) {
  //       //console.log('No hay sorteos disponibles:', response.lnResponseSD.responseHeadOV.respCodeDescription);
  //       this.filteredGames = [];

  //       // Actualizar estado si el filtro activo es lotería
  //       if (this.selectedFilter === 'loteria') {
  //         this.noLoteriaDrawsAvailable = true;
  //         //this.loteriaErrorMessage = response.lnResponseSD.responseHeadOV.respCodeDescription;
  //         this.loteriaErrorMessage = 'Sorteos no disponibles para la venta';
  //       }
  //       return;
  //     }

  //     const drawsDB = this.filteredGames.map((item: any) => ({
  //       ...item,
  //       normalizedName: this.cleanSpaces(item.name),
  //     }));

  //     // Verificar si existe el arreglo sorteo antes de mapearlo
  //     let drawsLotenal =
  //       response?.lnResponseSD?.sorteo?.map((item: any) => ({
  //         ...item,
  //         normalizedName: this.cleanSpaces(`sorteo${item.nombreSorteo}`),
  //         type: 'loteria', // Añadir tipo para identificar sorteos de lotería
  //       })) || [];

  //     // por si el array viene vacío aunque no haya error
  //     // aquí es el merge de los sortso disponibles de la api y firebase
  //     // si no existe en la api, no se muestra en el html
  //     if (drawsLotenal.length === 0) {
  //       console.log('No se encontraron sorteos en la respuesta');
  //       this.filteredGames = [];

  //       if (this.selectedFilter === 'loteria') {
  //         this.noLoteriaDrawsAvailable = true;
  //         this.loteriaErrorMessage = 'No se encontraron sorteos disponibles';
  //       }
  //       return;
  //     }

  //     // Filtrar solo el sorteo más cercano por cada nombreSorteo
  //     const closestDraws: Record<string, any> = {};

  //     drawsLotenal.forEach((sorteo: any) => {
  //       const nombreSorteo = sorteo.nombreSorteo;
  //       const fechaSorteo = new Date(sorteo.fechaCelebracion);

  //       if (!closestDraws[nombreSorteo] || fechaSorteo < new Date(closestDraws[nombreSorteo].fechaCelebracion)) {
  //         closestDraws[nombreSorteo] = sorteo;
  //       }
  //     });

  //     drawsLotenal = Object.values(closestDraws);

  //     const newDraws = drawsDB.filter((db: any) =>
  //       drawsLotenal.some((lotenal: any) => db.normalizedName === lotenal.normalizedName)
  //     );

  //     const mergedDraws = drawsLotenal.map((lotenal: any) => {
  //       const firebaseMatch = newDraws.find((db: any) => db.normalizedName === lotenal.normalizedName);

  //       return {
  //         ...lotenal,
  //         ...firebaseMatch,
  //         normalizedName: undefined,
  //       };
  //     });

  //     this.filteredGames = mergedDraws;

  //     // Verificar específicamente para el filtro de lotería
  //     if (this.selectedFilter === 'loteria') {
  //       const loteriaGames = this.filteredGames.filter((game) => game.type === 'loteria');
  //       if (loteriaGames.length === 0) {
  //         this.noLoteriaDrawsAvailable = true;
  //         this.loteriaErrorMessage = 'No hay sorteos de Cachito Electrónico disponibles';
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error al cargar juegos:', error);
  //     if (this.selectedFilter === 'loteria') {
  //       this.noLoteriaDrawsAvailable = true;
  //       this.loteriaErrorMessage = 'Error al cargar los sorteos de Cachito Electrónico';
  //     }
  //   } finally {
  //     this.isLoading = false;
  //   }
  // }

  async loadGamesLotenal() {
    this.isLoading = true;
    this.filteredGames = [];
    this.noLoteriaDrawsAvailable = false;

    this.lotenalDrawsSub?.unsubscribe();

    this.lotenalDrawsSub = this.drawsAS.getCurrentAvailableDrawsRealtime().subscribe(async (drawsDocument) => {
      try {
        if (!drawsDocument || !this.drawsAS.isDrawsResponseAvailable(drawsDocument)) {
          this.filteredGames = [];
          this.noLoteriaDrawsAvailable = true;
          this.loteriaErrorMessage = 'Sorteos no disponibles para la venta';
          return;
        }

        const cmsGames = this.getLoteriaCmsGames();
        if (!cmsGames.length && !this.games.length) {
          this.filteredGames = [];
          this.noLoteriaDrawsAvailable = false;
          this.isLoading = true;
          return;
        }
        let drawsLotenal = this.drawsAS.formatAllSorteos(drawsDocument).map((sorteo) => ({
          ...sorteo,
          normalizedName: this.cleanSpaces(`sorteo${sorteo.nombreSorteo}`),
          type: 'loteria' as const,
        }));

        if (drawsLotenal.length === 0) {
          this.filteredGames = [];
          this.noLoteriaDrawsAvailable = true;
          this.loteriaErrorMessage = 'No se encontraron sorteos disponibles';
          return;
        }

        const closestDraws: Record<string, (typeof drawsLotenal)[number]> = {};

        drawsLotenal.forEach((sorteo) => {
          const nombreSorteo = sorteo.nombreSorteo;
          const fechaSorteo = this.dateS.parseLotenalDateString(sorteo.fechaCelebracion);

          if (
            !fechaSorteo ||
            !closestDraws[nombreSorteo] ||
            fechaSorteo <
              (this.dateS.parseLotenalDateString(closestDraws[nombreSorteo].fechaCelebracion) ?? new Date(0))
          ) {
            closestDraws[nombreSorteo] = sorteo;
          }
        });

        drawsLotenal = Object.values(closestDraws);

        const logosData = await this.lotenalS.getAllLogos().catch(() => []);
        const mergedDraws = drawsLotenal
          .map((sorteo) => this.mergeLoteriaGameWithCms(sorteo, cmsGames, logosData))
          .filter((game): game is GameHome => !!game);

        this.filteredGames = mergedDraws;
        this.noLoteriaDrawsAvailable = mergedDraws.length === 0;
        this.loteriaErrorMessage = 'No hay sorteos de Cachito Electrónico disponibles';
      } catch (error) {
        console.error('Error al cargar juegos:', error);
        this.filteredGames = [];
        this.noLoteriaDrawsAvailable = true;
        this.loteriaErrorMessage = 'Error al cargar los sorteos de Cachito Electrónico';
      } finally {
        this.isLoading = false;
      }
    });

    this.subscriptions.push(this.lotenalDrawsSub);
  }

  private getLoteriaCmsGames(): GameHome[] {
    return this.games.filter((game) => game.type === 'loteria' && game.isActive);
  }

  private mergeLoteriaGameWithCms(
    sorteo: Draw & { normalizedName?: string },
    cmsGames: GameHome[],
    logosData: any[] = []
  ): GameHome | null {
    const cmsMatch = cmsGames.find((game) => this.cleanSpaces(game.name) === sorteo.normalizedName);

    if (!cmsMatch) {
      return null;
    }

    let dynamicLogoUrl: string | undefined = undefined;
    if (logosData && logosData.length && cmsMatch.path && cmsMatch.path.length >= 4) {
      const sorteoType = cmsMatch.path[3];
      const matchingLogo = logosData.find((logoDoc: any) => logoDoc['sorteo-'] === sorteoType);
      if (matchingLogo) {
        dynamicLogoUrl = matchingLogo.iconUrl || matchingLogo.logoUrl;
      }
    }

    return {
      id: cmsMatch.id,
      name: cmsMatch.name,
      logo: cmsMatch.logo,
      dynamicLogoUrl,
      message: cmsMatch.message,
      amount: cmsMatch.amount,
      price: cmsMatch.price,
      path: cmsMatch.path,
      isActive: cmsMatch.isActive,
      bag: cmsMatch.bag,
      product: cmsMatch.product,
      majorPrize: cmsMatch.majorPrize,
      serie: cmsMatch.serie,
      type: 'loteria',
      idDotacion: sorteo.idDotacion,
      tipoSorteo: sorteo.tipoSorteo,
      numeroSorteo: sorteo.numeroSorteo,
      fechaCelebracion: sorteo.fechaCelebracion,
      nombreSorteo: sorteo.nombreSorteo,
      precioFraccion: sorteo.precioFraccion,
      digitosBillete: sorteo.digitosBillete,
      digitosTerminacion: sorteo.digitosTerminacion,
      codigoDotacion: sorteo.codigoDotacion,
      limiteInfFechaCierre: sorteo.limiteInfFechaCierre,
      limiteSupFechaCierre: sorteo.limiteSupFechaCierre,
      signos: sorteo.signos,
      close: this.parseLotenalDateToTimestamp(sorteo.limiteSupFechaCierre),
      next: this.parseLotenalDateToTimestamp(sorteo.fechaCelebracion),
      isClosed: this.sorteoS.isDrawClosed(sorteo.limiteInfFechaCierre),
    };
  }

  isLoteriaGameClosed(game: GameHome): boolean {
    if (game.type !== 'loteria') {
      return false;
    }

    if (game.isClosed !== undefined) {
      return game.isClosed;
    }

    return game.limiteInfFechaCierre ? this.sorteoS.isDrawClosed(game.limiteInfFechaCierre) : false;
  }

  cleanSpaces(name: string) {
    return name.toLowerCase().split(' ').join(',').replace(/,/g, '').trim();
  }

  async selectFilter(filter: 'electronico' | 'loteria') {
    if (this.selectedFilter === filter && (this.filteredGames.length > 0 || this.noLoteriaDrawsAvailable)) {
      return;
    }

    this.selectedFilter = filter;
    this.filteredGames = [];

    if (filter === 'loteria') {
      this.loadGamesLotenal();
      return;
    }

    this.filteredGames = this.games.filter((g) => g.type === filter);
    this.noLoteriaDrawsAvailable = false;
  }

  async openPopover(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: ProfileComponent,
      event,
    });

    await popover.present();

    const { role } = await popover.onWillDismiss();

    switch (role) {
      case 'logout':
        await this.authS.logOut();
        await this.navCtrl.navigateRoot(['/home']);

        // Abrir modal de login automáticamente
        if (!this.modalOpen) {
          this.modalOpen = true;

          const modal = await this.modalCtrl.create({
            component: LoginModalComponent,
            backdropDismiss: false,
            cssClass: 'login-modal',
          });

          await modal.present();

          const { data } = await modal.onDidDismiss();
          this.modalOpen = false;
        }
        break;
      case 'profile':
        this.navCtrl.navigateBack(['/account/profile']);
        break;

      default:
        break;
    }
  }

  updateCountdowns() {
    const referenceNow = this.now ?? new Date();
    const now = referenceNow.getTime();

    const horariosFijos: { [key: string]: string } = {
      'Tris Medio día': '13:00',
      'Tris de las Tres': '15:00',
      'Tris Extra': '17:00',
      'Tris de las Siete': '19:00',
      'Tris Clásico': '21:00',
      'Chispazo de las Tres': '15:00',
      'Chispazo Clásico': '21:00',
    };

    const juegosConCloseDirecto = ['Progol', 'Progol Media Semana', 'ProTouch', 'Prono Sports'];

    // Juegos que siempre cierran a las 21:00 y usan horario fijo
    const juegosHorario21 = ['Melate Revancha Revanchita', 'Melate Retro', 'Gana Gato'];

    this.games.forEach((game) => {
      const nombreGeneral = game.name;
      let closeTime: number | null = null;

      // Juegos tipo lotería: cierre real según límite superior de Lotenal (hora actual, no simulada)
      if (game.type === 'loteria' && game.limiteSupFechaCierre) {
        const closeDate = this.parseLotenalDate(game.limiteSupFechaCierre);
        if (closeDate) {
          closeTime = closeDate.getTime();
          const referenceNow = this.loteriaNow ?? new Date();
          const diff = closeTime - referenceNow.getTime();

          if (diff <= 0) {
            this.countdownTimers[nombreGeneral] = 'Cerrado';
          } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            if (days > 0) {
              this.countdownTimers[nombreGeneral] = `${days}d ${hours}h ${minutes}m ${seconds}s`;
            } else {
              this.countdownTimers[nombreGeneral] = `${hours}h ${minutes}m ${seconds}s`;
            }
          }
        }
        return;
      }

      // Juegos con fecha de cierre directa
      if (juegosConCloseDirecto.includes(nombreGeneral)) {
        if (game.close) {
          closeTime = game.close.toDate().getTime();
          const diff = closeTime - now;

          if (diff <= 0) {
            this.countdownTimers[nombreGeneral] = 'Cerrado';
          } else {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            if (days > 0) {
              this.countdownTimers[nombreGeneral] = `${days}d ${hours}h ${minutes}m ${seconds}s`;
            } else {
              this.countdownTimers[nombreGeneral] = `${hours}h ${minutes}m ${seconds}s`;
            }
          }
        }
        return;
      }

      // Juegos con cierre fijo a las 21:00
      if (juegosHorario21.includes(nombreGeneral)) {
        const fecha = new Date();
        fecha.setHours(21, 0, 0, 0);
        if (fecha.getTime() <= now) {
          fecha.setDate(fecha.getDate() + 1);
        }
        const diff = fecha.getTime() - now;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        this.countdownTimers[nombreGeneral] = `${hours}h ${minutes}m ${seconds}s`;
        return;
      }

      // Juegos con horarios fijos genéricos (Tris, Chispazo)
      let siguienteSorteo: string | null = null;
      let siguienteHora: number | null = null;

      Object.entries(horariosFijos).forEach(([nombre, hora]) => {
        if (nombre.startsWith(nombreGeneral)) {
          const [h, m] = hora.split(':').map(Number);
          const fecha = new Date();
          fecha.setHours(h, m, 0, 0);
          if (fecha.getTime() <= now) fecha.setDate(fecha.getDate() + 1);

          if (!siguienteHora || fecha.getTime() < siguienteHora) {
            siguienteSorteo = nombre;
            siguienteHora = fecha.getTime();
          }
        }
      });

      if (siguienteHora) {
        const diff = siguienteHora - now;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (days > 0) {
          this.countdownTimers[nombreGeneral] = `${days}d ${hours}h ${minutes}m ${seconds}s`;
        } else {
          this.countdownTimers[nombreGeneral] = `${hours}h ${minutes}m ${seconds}s`;
        }
      }
    });
  }

  calculateTimeDifference(limiteSupFechaCierre: string): string {
    const fechaLimite = this.parseLotenalDate(limiteSupFechaCierre);
    if (!fechaLimite) {
      return '';
    }

    const fechaActual = this.loteriaNow ?? new Date();
    const diferenciaMs = fechaLimite.getTime() - fechaActual.getTime();

    if (diferenciaMs <= 0) {
      return 'Cerrado';
    }

    const dias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));
    const horas = Math.floor((diferenciaMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutos = Math.floor((diferenciaMs % (1000 * 60 * 60)) / (1000 * 60));
    const segundos = Math.floor((diferenciaMs % (1000 * 60)) / 1000);

    return `${dias}dias ${horas}h ${minutos}m ${segundos}s`;
  }

  getCountdownReferenceDate(game: GameHome): Date {
    if (game.type === 'loteria') {
      return this.loteriaNow ?? new Date();
    }

    return this.now ?? new Date();
  }

  getGameCloseDate(game: GameHome): Date | null {
    if (game.type === 'loteria') {
      if (game.limiteSupFechaCierre) {
        return this.parseLotenalDate(game.limiteSupFechaCierre) ?? null;
      }

      return null;
    }

    return game.close?.toDate() ?? null;
  }

  getGameNextDate(game: GameHome): Date | null {
    if (game.type === 'loteria') {
      if (game.fechaCelebracion) {
        return this.parseLotenalDate(game.fechaCelebracion) ?? null;
      }

      return null;
    }

    return game.next?.toDate() ?? null;
  }

  parseLotenalDate(dateStr?: string): Date | undefined {
    return this.dateS.parseLotenalDateString(dateStr);
  }

  parseLotenalDateToTimestamp(dateStr?: string): Timestamp | undefined {
    const date = this.parseLotenalDate(dateStr);
    return date ? Timestamp.fromDate(date) : undefined;
  }

  trackGameSelected(game: any) {
    // console.log('Tracking game selected:', game.name);
    this.trackingS.trackEvent(
      'game_selected',
      {
        // user_id: uid,
        game_id: game.name,
        selection_details: {
          game_type: game.type,
        },
        // timestamp: hour,
      },
      false,
      false
    );
  }

  trackGameViewed(game: any) {
    if (this.trackedGame === game.name) {
      return; // ya se envió
    }

    this.trackedGame = game.name;
    // this.trackingS.trackEvent(
    //   'game_viewed',
    //   {
    //     // user_id: uid,
    //     game_id: game.name,
    //     selection_details: {
    //       game_type: game.type,
    //     },
    //     // timestamp: hour,
    //   },
    //   false,
    //   false
    // );
  }

  ensureTimestampOrUndefined(date: any): Timestamp | undefined {
    if (!date) return undefined;
    if (date instanceof Timestamp) return date;
    if (date instanceof Date) return Timestamp.fromDate(date);
    if (typeof date === 'string') {
      try {
        return Timestamp.fromDate(new Date(date));
      } catch {
        return undefined;
      }
    }
    return undefined;
  }
  loadMoreSlides() {
    this.isLoading = false;

    setTimeout(() => {
      const newSlides = [...this.gamesSlide];

      this.additionalSlides = [...this.additionalSlides, ...newSlides];
      this.isLoading = true;
    }, 1500);
  }

  private async getAndCacheImage(url: string, key: string): Promise<string> {
    const cached = localStorage.getItem(`cache_img_${key}`);
    if (cached) return cached;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64data = reader.result as string;
          try {
            localStorage.setItem(`cache_img_${key}`, base64data);
          } catch (e) {
            // Si el LocalStorage se llena, limpiamos o ignoramos
            console.warn('LocalStorage lleno, no se pudo guardar imagen');
          }
          resolve(base64data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      return url; // Si falla, devolvemos la URL original para que intente cargar normal
    }
  }

  loadGamesHeader() {
    const headerSub = this.gameHomeService.getGamesHeader().subscribe((data: GameHome[]) => {
      this.sorteosHeader = data.filter((item) => item);
    });
    this.subscriptions.push(headerSub);
  }

  async loadLives() {
    try {
      this.lives = await this.liveS.getValidLives();
    } catch (e) {
      console.error('Error loading lives:', e);
    }
  }
}
